import { AcatDirection, type InvestmentActivityDetailV2, InvestmentTransferStatus } from '@egw/api'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { Currency, dateFormat } from '@egw/utils'
import cn from 'classnames'
import { type FC, useMemo } from 'react'
import { useNavigate } from 'react-router'

export interface TransactionInvestmentItemProps {
  transaction: InvestmentActivityDetailV2
  alias: number
}

export const TransactionInvestmentItem: FC<TransactionInvestmentItemProps> = ({ transaction, alias }) => {
  const navigate = useNavigate()
  const { t } = useTranslation('features-transactions')

  const transactionDate = transaction.date

  const isProcessing = ((): boolean => {
    if (transaction.__typename === 'InvestmentTransferActivityDetailV2') {
      return (
        transaction.transferStatus === InvestmentTransferStatus.PROCESSING_FUNDS ||
        transaction.transferStatus === InvestmentTransferStatus.PROCESSING_TRADES
      )
    }
    return false
  })()

  const label = ((): string => {
    if (transaction.__typename === 'InvestmentTransferActivityDetailV2')
      return t(`recent_activity.labels.${transaction.transferType}`)

    if (transaction.__typename === 'InvestmentDividendActivityDetailV2') return t('recent_activity.labels.dividends')

    if (transaction.__typename === 'InvestmentInterestActivityDetail') return t('recent_activity.labels.interest')

    if (transaction.__typename === 'InvestmentAcatGroupActivityDetailV2') return t('recent_activity.labels.acat')

    // ELSE __typname === 'InvestmentFeeActivityDetail'
    return t('recent_activity.labels.fee')
  })()

  const displayAmount = useMemo((): string => {
    // Handle ACAT transfers with special formatting
    if (transaction.__typename === 'InvestmentAcatGroupActivityDetailV2') {
      const { transfers } = transaction

      // If no transfers, fall through to default amount display
      if (transfers.length > 0) {
        // BR-03: Cash-Only Transfers
        if (transfers.length === 1 && transfers[0].__typename === 'InvestmentActivityAcatCash') {
          const cashTransfer = transfers[0]
          const prefix = cashTransfer.direction === AcatDirection.INCOMING ? '+' : '-'

          return `${prefix} ${Currency.integerCents.onlyMinus(cashTransfer.amount?.amount ?? 0)}`
        }

        // BR-04: Single Security Transfers
        if (transfers.length === 1 && transfers[0].__typename === 'InvestmentActivityAcatStock') {
          const stockTransfer = transfers[0]
          const prefix = stockTransfer.direction === AcatDirection.INCOMING ? '+' : '-'
          return `${stockTransfer.symbol}, ${prefix} ${stockTransfer.quantity.toString()}`
        }

        // BR-05: Multiple Asset Transfers
        if (transfers.length > 1) {
          // Determine direction from first transfer (all should have same direction in a group)
          const prefix = transfers[0].direction === AcatDirection.INCOMING ? '+' : '-'
          return t('acat_transfer.multiple_assets', { prefix })
        }
      }
    }

    // Default amount display for non-ACAT transactions

    return Currency.integerCents.plusMinus(transaction.amount?.amount ?? 0)
  }, [transaction, t])

  const displayNameClasses = cn('typo-primary-base-regular self-start block text-text-body truncate mr-1 md:mr-0', {
    'col-span-9': displayAmount.length <= 5,
    'col-span-8': displayAmount.length > 5 && displayAmount.length <= 10,
    'col-span-7': displayAmount.length > 10,
  })
  const amountClasses = cn('typo-primary-base-semibold text-text-body', {
    'text-body': !isProcessing,
    'text-meta': isProcessing,
    'col-span-4': displayAmount.length > 10,
    'col-span-3': displayAmount.length > 5 && displayAmount.length <= 10,
    'col-span-2': displayAmount.length <= 5,
  })
  const labelClasses = cn('typo-primary-sm-regular text-text-meta text-left flex pl-1 capitalize', {
    'col-span-9': displayAmount.length <= 5,
    'col-span-8': displayAmount.length > 5 && displayAmount.length <= 10,
    'col-span-7': displayAmount.length > 10,
  })
  const statusClasses = cn('text-green-500 typo-primary-sm-semibold', {
    'text-alertSuccessTitle': isProcessing,
    'col-span-4': displayAmount.length > 10,
    'col-span-3': displayAmount.length > 5 && displayAmount.length <= 10,
    'col-span-2': displayAmount.length <= 5,
  })

  return (
    <div className="border-light border-b last-of-type:border-none">
      <button
        id={transaction.activityId.toString()}
        type="button"
        onClick={() => {
          void navigate(
            WEALTH_ROUTES.INVESTMENTS_ACCOUNT_ACTIVITY_DETAILS.path
              .replace(':alias', alias.toString())
              .replace(':transactionId', transaction.activityId.toString()),
          )
        }}
        className="w-full transaction_link"
      >
        <div className="w-full py-4 gap-1 grid grid-cols-12 flex-row justify-center">
          <div className="flex flex-row col-span-1 min-w-0 items-center">
            {/* Date Column */}
            <div className="flex-col text-text-meta flex self-start text-left">
              {/* Day */}
              <p className="typo-primary-lg-regular">
                {transaction.date ? dateFormat(new Date(transactionDate), t('date.day')) : null}
              </p>
              {/* Month */}
              <p className="typo-primary-xs-bold text-center">
                {transaction.date ? dateFormat(new Date(transactionDate), t('date.month')).toUpperCase() : null}
              </p>
            </div>
          </div>

          {/* Top Row */}
          <div className="flex flex-row col-span-11 col-start-2 pl-1 justify-between truncate relative top-[1px]">
            {/* Display Name */}
            <p className={displayNameClasses}>{transaction.displayName}</p>
            {/* Amount */}
            <p className={amountClasses}>{displayAmount}</p>
          </div>

          {/* Bottom Row */}
          <div className="flex flex-row text-right self-start col-span-11 col-start-2  justify-between relative top-[-1.3rem] mb-[-1.3rem]">
            {/* Label */}
            <p className={labelClasses}>{label}</p>
            {/* Status */}
            {isProcessing ? <p className={statusClasses}>{t(`activity.processing`)}</p> : null}
          </div>
        </div>
      </button>
    </div>
  )
}
